import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useTelegramTheme } from '../../../shared/utils/telegram';
import { api } from '../../../shared/services/api';

function NetworkPorts() {
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);

  const themeParams = useTelegramTheme();

  // Map new theme params to old naming convention for compatibility
  const theme = {
    text_color: themeParams.textColor,
    hint_color: themeParams.hintColor,
    link_color: themeParams.linkColor,
    section_bg_color: themeParams.sectionBackgroundColor,
    section_separator_color: themeParams.sectionSeparatorColor,
    bg_color: themeParams.backgroundColor,
  };

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const response = await api.get('/api/stats/ports');
        setPorts(response.ports || []);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch ports:', error);
        setLoading(false);
      }
    };

    fetchPorts();
    const interval = setInterval(fetchPorts, 15000); // Update every 15 seconds

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={{ color: theme.text_color }} className="p-4">
        Загрузка портов...
      </div>
    );
  }

  // Group ports by protocol
  const tcpPorts = ports.filter(p => p.protocol.includes('TCP'));
  const udpPorts = ports.filter(p => p.protocol.includes('UDP'));

  // Prepare data for chart
  const chartData = ports.slice(0, 10).map(p => ({
    port: p.port,
    name: p.service || `Port ${p.port}`,
    value: 1,
    protocol: p.protocol,
    process: p.process
  }));

  return (
    <div className="space-y-4">
      <div
        style={{
          backgroundColor: theme.section_bg_color,
          borderColor: theme.section_separator_color
        }}
        className="rounded-lg border p-4"
      >
        <h3 style={{ color: theme.text_color }} className="text-lg font-semibold mb-4">
          Открытые порты
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p style={{ color: theme.hint_color }} className="text-sm">
              Всего
            </p>
            <p style={{ color: theme.link_color }} className="text-3xl font-bold">
              {ports.length}
            </p>
          </div>
          <div className="text-center">
            <p style={{ color: theme.hint_color }} className="text-sm">
              TCP
            </p>
            <p style={{ color: '#10b981' }} className="text-3xl font-bold">
              {tcpPorts.length}
            </p>
          </div>
          <div className="text-center">
            <p style={{ color: theme.hint_color }} className="text-sm">
              UDP
            </p>
            <p style={{ color: '#f59e0b' }} className="text-3xl font-bold">
              {udpPorts.length}
            </p>
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <div
          style={{
            backgroundColor: theme.section_bg_color,
            borderColor: theme.section_separator_color
          }}
          className="rounded-lg border p-4"
        >
          <h3 style={{ color: theme.text_color }} className="text-lg font-semibold mb-4">
            Топ портов
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.section_separator_color} />
              <XAxis
                dataKey="port"
                stroke={theme.hint_color}
                tick={{ fill: theme.hint_color }}
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke={theme.hint_color}
                tick={{ fill: theme.hint_color }}
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.section_bg_color,
                  border: `1px solid ${theme.section_separator_color}`,
                  borderRadius: '8px',
                  color: theme.text_color
                }}
                labelStyle={{ color: theme.text_color }}
                formatter={(value, name, props) => [props.payload.name, 'Service']}
              />
              <Bar dataKey="value" name="Status">
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.protocol.includes('TCP') ? '#10b981' : '#f59e0b'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div
        style={{
          backgroundColor: theme.section_bg_color,
          borderColor: theme.section_separator_color
        }}
        className="rounded-lg border p-4"
      >
        <h3 style={{ color: theme.text_color }} className="text-lg font-semibold mb-4">
          Список портов
        </h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {ports.map((port, index) => (
            <div
              key={index}
              style={{
                backgroundColor: theme.bg_color,
                borderColor: theme.section_separator_color
              }}
              className="rounded-lg border p-3 flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <div
                  style={{
                    backgroundColor: port.protocol.includes('TCP') ? '#10b981' : '#f59e0b'
                  }}
                  className="px-2 py-1 rounded text-white text-xs font-medium"
                >
                  {port.protocol}
                </div>
                <div>
                  <p style={{ color: theme.text_color }} className="font-medium">
                    Port {port.port}
                  </p>
                  <p style={{ color: theme.hint_color }} className="text-sm">
                    {port.service || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p style={{ color: theme.text_color }} className="text-sm">
                  {port.ip}
                </p>
                {port.process && port.process !== 'N/A' && (
                  <p style={{ color: theme.hint_color }} className="text-xs">
                    {port.process}
                  </p>
                )}
              </div>
            </div>
          ))}
          {ports.length === 0 && (
            <p style={{ color: theme.hint_color }} className="text-center py-4">
              Нет открытых портов
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default NetworkPorts;
